import React, {useEffect, useState} from "react";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {formatDataIsoBR, formatReais} from "../../../shared/utils/formatters";
import {
    corpoDoPagamento,
    formularioDoPagamento,
    FORMAS_DO_PAGAMENTO_MANUAL,
    getMeioDaCobrancaLabel,
    hojeIso,
    validarPagamento,
} from "../cobrancasUtils";

/*
 * Pagamento feito por fora da cobrança: transferência, Pix fora do boleto ou outro meio (BBF-33). O financeiro dá
 * baixa no boleto do BB quando há um, e a régua regulariza a assinatura (decisão do usuário, 21/10/2026).
 */
export default function RegistrarPagamentoDialog({open, cobranca, onClose, onConfirm}) {
    const hoje = hojeIso();
    const [form, setForm] = useState(() => formularioDoPagamento(cobranca, hoje));
    const [erros, setErros] = useState({});
    const [erroDaApi, setErroDaApi] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(formularioDoPagamento(cobranca, hojeIso()));
        setErros({});
        setErroDaApi('');
    }, [open, cobranca]);

    if (!open || !cobranca) return null;

    const mudar = (campo) => (event) => setForm((atual) => ({...atual, [campo]: event.target.value}));

    const confirmar = async () => {
        const encontrados = validarPagamento(form, cobranca, hoje);
        setErros(encontrados);
        if (Object.keys(encontrados).length) return;
        setEnviando(true);
        setErroDaApi('');
        const erro = await onConfirm(cobranca, corpoDoPagamento(form));
        setEnviando(false);
        if (erro) setErroDaApi(erro);
    };

    return (
        <Dialog open={open} onClose={enviando ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Registrar pagamento</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography variant="body2" color="text.secondary">
                        {getMeioDaCobrancaLabel(cobranca.meio)} de {formatReais(cobranca.valor)}, com vencimento em {formatDataIsoBR(cobranca.vencimento)}.
                        Use para o pagamento que a empresa fez por fora da cobrança. O pagamento do boleto chega sozinho pelo banco.
                    </Typography>
                    {cobranca.meio === 'BOLETO' && cobranca.boleto ? (
                        <Alert severity="info">O boleto desta cobrança é baixado no banco, para a empresa não pagar duas vezes.</Alert>
                    ) : null}
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField
                            label="Valor pago"
                            type="number"
                            value={form.valorPago}
                            onChange={mudar('valorPago')}
                            error={Boolean(erros.valorPago)}
                            helperText={erros.valorPago || 'Precisa cobrir a cobrança inteira.'}
                            inputProps={{min: 0, step: '0.01'}}
                            fullWidth
                        />
                        <TextField
                            label="Data do pagamento"
                            type="date"
                            value={form.dataPagamento}
                            onChange={mudar('dataPagamento')}
                            error={Boolean(erros.dataPagamento)}
                            helperText={erros.dataPagamento}
                            inputProps={{max: hoje}}
                            InputLabelProps={{shrink: true}}
                            fullWidth
                        />
                    </Stack>
                    <TextField
                        select
                        label="Como foi pago"
                        value={form.formaPagamento}
                        onChange={mudar('formaPagamento')}
                        error={Boolean(erros.formaPagamento)}
                        helperText={erros.formaPagamento}
                        fullWidth
                    >
                        {FORMAS_DO_PAGAMENTO_MANUAL.map((forma) => (
                            <MenuItem key={forma.valor} value={forma.valor}>{forma.rotulo}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Identificador da transação (opcional)"
                        value={form.idTransacao}
                        onChange={mudar('idTransacao')}
                        error={Boolean(erros.idTransacao)}
                        helperText={erros.idTransacao || 'O código do Pix ou da transferência, se houver.'}
                        fullWidth
                    />
                    <TextField
                        label="Motivo"
                        value={form.motivo}
                        onChange={mudar('motivo')}
                        error={Boolean(erros.motivo)}
                        helperText={erros.motivo || 'Fica no histórico da cobrança.'}
                        multiline
                        minRows={2}
                        fullWidth
                    />
                    {erroDaApi ? <Alert severity="error">{erroDaApi}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
                <Button variant="contained" color="success" onClick={confirmar} disabled={enviando}>
                    {enviando ? 'Registrando...' : 'Registrar pagamento'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
